import React, {useContext} from 'react';
import {translateService} from "../../services/TranslateService";
import ApiService from "../../services/api/ApiService";
import {useDispatch} from "react-redux";
import "./PaymentCallbacks.scss";
import {useClarityStep} from "../../services/clarity/useClarityStep";
import {resetUrlForResCancelled, resetUrlForPaymentRetry} from "../../GetUrlParams";
import { DataContext } from "../../contexts/dataContext";
import { parseRidSession } from "../../interfaces/RidSessionData";

interface PaymentFailProps {
    rootUrl: string,
    returnToHome: Function;
    apiService: ApiService | undefined;
    pageType: string;
    changeVirtualPageInfo: Function;
    setShowPaymentResultScreen: Function;
    clearPaytrailProviders: () => void;
}

export default function PaymentFail ({rootUrl, returnToHome, pageType, apiService, changeVirtualPageInfo,  setShowPaymentResultScreen, clearPaytrailProviders} : PaymentFailProps ) {
    const dispatch = useDispatch();
    const { changeResCancelled } = useContext(DataContext);
    const parsedData = parseRidSession(sessionStorage.getItem('rid-1'));
    let resId = parsedData?.resId;
    const lang = parsedData?.lang ?? 'fi';

    // Mirrors PaymentRetry.cancelRes: hits BE to cancel the in-flight reservation,
    // then sets ?resCancelled=true and dataContext flag so App.tsx routes the
    // customer to the ReservationCancelled screen. From there the existing
    // "return to home" path lets them start a fresh reservation.
    const cancelRes = () => {
        dispatch({
            type: 'SET_API_MESSAGE',
            payload: translateService.translate('paymentCallbackScreens.cancellingRes'),
        });

        apiService?.reservation.cancelReservation(resId ?? -1).then((cancelResp) => {
            if (cancelResp?.cancelled === true) {
                resetUrlForResCancelled();
                changeResCancelled(true);
                return;
            }
            // BE accepted the call but refused to cancel — typical reasons: the
            // payment window has elapsed (BE auto-finalises) or the reservation
            // has already been paid for via another route.
            dispatch({
                type: 'SET_RESPONSE_MODAL',
                payload: translateService.translate('paymentCallbackScreens.cancelResFailed'),
            });
        }).catch((err: unknown) => {
            dispatch({
                type: 'SET_RESPONSE_MODAL',
                payload: err instanceof Error ? err.message : translateService.translate('paymentCallbackScreens.cancelResFailed'),
            });
        }).finally(() => {
            dispatch({
                type: 'SET_API_MESSAGE',
                payload: '',
            });
        });
    };

    useClarityStep('resultFailed', {
        paymentMethod: parsedData?.paymentMethod,
        city: parsedData?.pickupLoc || '',
        start: parsedData?.startDate || '',
        end: parsedData?.endDate || '',
        loggedIn: parsedData?.li ?? false,
        oneway: parsedData?.returnLoc ? true : undefined,
        resId: String(resId ?? '')
    });

    React.useEffect(() => {
        let scrollPoint = document.getElementById("paymentFailScrollPoint");
        if (scrollPoint) {
            scrollPoint.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        changeVirtualPageInfo('paymentFailure');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sectionClass = `payment-callback-section py-3 mobile-top-line`;
    return (
        <div className='callback-info-container pb-3' id='paymentFailScrollPoint'>
            <div className={sectionClass}>
                <div className="logo-heading-row">
                    <img width="35" height="35" src={rootUrl + '/assets/icons/warning-icon.png'} alt="24Rent logo" loading="lazy" />
                    <h1 className='payment-callback-heading text-center'>
                        {translateService.translate('paymentCallbackScreens.paymentFailedHeading')}
                    </h1>
                </div>
                <span className='text-center'>
                    {translateService.translate('paymentCallbackScreens.paymentFailedText1')}
                </span>

                <div className='d-flex justify-content-center'>
                    <button className="btn btn-primary btn-outline-primary btn-cta mt-2 payment-fail-action-btn"
                            onClick={() => {
                                resetUrlForPaymentRetry();
                                clearPaytrailProviders();
                                setShowPaymentResultScreen();
                            }}>
                        {translateService.translate('paymentCallbackScreens.paymentFailedBtn1')}
                    </button>
                </div>
                <div className={'gray-border-list'}>
                    <span>{translateService.translate('paymentCallbackScreens.paymentFailedText2')}</span>
                    <ul className="thanks-bullets-list">
                        {[1, 2, 3].map(i => (
                            <li key={i}>
                                - {translateService.translate(`paymentCallbackScreens.paymentFailedBullet${i}`)}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className='d-flex justify-content-center'>
                    <button className="btn btn-dark btn-outline-dark btn-cta mt-2 payment-fail-action-btn"
                            onClick={cancelRes}>
                        {translateService.translate('paymentCallbackScreens.cancelResAndMakeNew')}
                    </button>
                </div>
                <div className='d-flex justify-content-center'>
                    <button className="btn btn-dark btn-outline-dark btn-cta mt-2 payment-fail-action-btn"
                            onClick={() => {
                                if (lang === 'en') {window.open('https://www.24rent.fi/en/customer_service/', '_blank')}
                                else {window.open('https://www.24rent.fi/asiakaspalvelu', '_blank')}
                            }}>
                        {translateService.translate('paymentCallbackScreens.contactCustomerService')}
                    </button>
                </div>
            </div>
        </div>
    );
}